import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  PreSignedResponse,
  SoilAnalysesGroup,
  SoilAnalysis,
  SoilAnalysisGroupData,
} from '../_models/fieldAnalysis';
import { environment } from './../../../environments/environment';
import { CommonChemicalElement } from '../_models/commonService';

@Injectable()
export class SoilAnalysisService {
  private apiBaseUrl = environment.apiBaseUrl;

  private http: HttpClient = inject(HttpClient);
  public soilAnalyses: BehaviorSubject<SoilAnalysis[]> = new BehaviorSubject<SoilAnalysis[]>([]);
  public soilAnalysisElements: BehaviorSubject<CommonChemicalElement[]> = new BehaviorSubject<
    CommonChemicalElement[]
  >(null);

  /**
   * Gets all the soilAnalysis available for the current company
   *
   * @returns {Observable<SoilAnalysis[]>} - Returns an Observable with all the soilAnalysis available for the current company
   */
  getAllAnalysis(): Observable<SoilAnalysis[]> {
    return this.http.get<SoilAnalysis[]>(this.apiBaseUrl + 'api/private/v1/soilAnalysis').pipe(
      map((response: SoilAnalysis[]) => {
        this.soilAnalyses.next(response);
        return response;
      })
    );
  }

  /**
   * Gets all the soilAnalysis conducted on the specified field
   *
   * @param {any} fieldId - The id of the field for which the soilAnalysis needs to be fetched
   * @returns {Observable<SoilAnalysis[]>} - Returns an Observable with all the soilAnalysis conducted on the specified field
   */
  getAnalysis(fieldId: any): Observable<SoilAnalysis[]> {
    const params = new HttpParams().set('fieldId', fieldId.toString());
    return this.http
      .get<SoilAnalysis[]>(`${this.apiBaseUrl}api/private/v1/soilAnalysis`, { params })
      .pipe(map((response) => response));
  }

  /**
   * Gets all the soilAnalysis grouped by UUID.
   *
   * @param {string} UUID - The UUID of the grouped soilAnalysis.
   * @returns {Observable<SoilAnalysis[]>} - Returns an Observable with all the soilAnalyses belonging to a specific UUID.
   */
  getAnalysesGroup(UUID: string): Observable<SoilAnalysis[]> {
    return this.http.get<SoilAnalysis[]>(
      `${this.apiBaseUrl}api/private/v1/soilAnalysis/grouped/${UUID}`
    );
  }

  /**
   * Gets a single soilAnalysis by its ID.
   *
   * @param {string} id - The ID of the soilAnalysis.
   * @returns {Observable<SoilAnalysis>} - Returns an Observable with the single soilAnalysis.
   */
  getSingleAnalysis(id: string): Observable<SoilAnalysis> {
    return this.http
      .get<SoilAnalysis>(`${this.apiBaseUrl}api/private/v1/soilAnalysis/${id}`)
      .pipe(map((response) => response));
  }

  /**
   * This method is used to save a single soil analysis.
   * If the analysis already has an ID, it will be updated using a PUT request.
   * If the analysis does not have an ID, it will be created using a POST request.
   *
   * @param {SoilAnalysis} analysis - The soil analysis to be saved.
   * @returns {Observable<SoilAnalysis>} - Returns an Observable with the saved soil analysis.
   */
  saveAnalysis(analysis: SoilAnalysis): Observable<SoilAnalysis> {
    if (analysis.id) {
      return this.http
        .put<SoilAnalysis>(`${this.apiBaseUrl}api/private/v2/soilAnalysis/${analysis.id}`, analysis)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<SoilAnalysis>(`${this.apiBaseUrl}api/private/v2/soilAnalysis`, analysis)
        .pipe(map((response) => response));
    } // TODO: check later .json()
  }

  /**
   * Saves multiple soil analyses in bulk.
   * Firstly, differentiates between new and existing analyses.
   * Then, checks if any of the analyses has a UUID.
   *
   * case 1: If none of the analyses has a UUID, it sends a POST request to create the new soil analyses (and the group) and then a PUT with UUID for the existing ones.
   * case 2: If any of the analyses has a UUID, it does a PUT for the existing ones and a POST with UUID for the new ones.
   *
   *
   * @param {SoilAnalysis[]} analyses - An array of SoilAnalysis objects representing the analyses to be saved.
   * @returns {Observable<SoilAnalysis[]>} - Returns an Observable with the created and/or updated analyses.
   */
  saveGroupAnalyses(analyses: SoilAnalysis[]): Observable<SoilAnalysis[]> {
    // Split the analyses into new and existing ones
    const newAnalyses = analyses.filter((analysis) => !analysis.id);
    const existingAnalyses = analyses.filter((analysis) => !!analysis.id);

    // Check if any of the analyses has a UUID
    const hasUUID = analyses.some((analysis) => !!analysis.uuid);

    // If none of the analyses has a UUID, create a new group
    if (!hasUUID) {
      return this.http
        .post(
          `${this.apiBaseUrl}api/private/v1/soilAnalysis/createSoilAnalysisGrouped`,
          newAnalyses
        )
        .pipe(
          switchMap((response: SoilAnalysis[]) => {
            const createdAnalysis = response;

            // Add the UUID to the existing analyses
            const existingAnalysisWithUUID = existingAnalyses.map((analysis) => {
              analysis.uuid = createdAnalysis[0].uuid;
              return analysis;
            });

            // If there are existing analyses, update them
            const updateCall = existingAnalysisWithUUID.length
              ? this.http.put(
                  `${this.apiBaseUrl}api/private/v1/soilAnalysis/updateSoilAnalysisGrouped`,
                  existingAnalysisWithUUID
                )
              : of([]);

            // Return the created analyses and the updated ones
            return forkJoin([of(createdAnalysis), updateCall]).pipe(
              map(([soilAnalyses]) => {
                return Array.isArray(soilAnalyses) ? soilAnalyses : [soilAnalyses];
              })
            );
          })
        );
    } else {
      // If any of the analyses has a UUID, update the existing analyses
      return this.http
        .put<
          SoilAnalysis[]
        >(`${this.apiBaseUrl}api/private/v1/soilAnalysis/updateSoilAnalysisGrouped`, existingAnalyses)
        .pipe(
          switchMap((response: SoilAnalysis[]) => {
            const updatedAnalysis = response;

            // If there are new analyses, link them to the existing group
            const responseNewAnalyses: Observable<SoilAnalysis[] | []> = newAnalyses.length
              ? this.saveAnalysesLinkToGroup(newAnalyses, existingAnalyses[0].uuid)
              : of([]);

            // Return the updated analyses and the linked ones
            return forkJoin([of(updatedAnalysis), responseNewAnalyses]).pipe(
              map(([updatedResponse, linkedResponse]) => {
                const updatedArray = Array.isArray(updatedResponse)
                  ? updatedResponse
                  : [updatedResponse];
                const linkedArray = Array.isArray(linkedResponse)
                  ? linkedResponse
                  : [linkedResponse];

                return [...updatedArray, ...linkedArray];
              })
            );
          })
        );
    }
  }

  /**
   * Saves multiple soil analyses and links them to a specific group identified by UUID.
   *
   * @param {SoilAnalysis[]} analyses - An array of SoilAnalysis objects representing the analyses to be saved.
   * @param {string} UUID - The UUID of the group to which the analyses will be linked.
   * @returns {Observable<SoilAnalysis[]>} - Returns an Observable with the saved analyses.
   */
  saveAnalysesLinkToGroup(analyses: SoilAnalysis[], UUID: string): Observable<SoilAnalysis[]> {
    return this.http
      .post<
        SoilAnalysis[]
      >(`${this.apiBaseUrl}api/private/v1/soilAnalysis/createSoilAnalysisGrouped/${UUID}`, analyses)
      .pipe(
        catchError((error: any) => {
          console.error('Error while linking analyses to group:', error);
          return of([]); // Return empty array instead of a string
        })
      );
  }

  /**
   * This method is used to delete a single soilAnalysis by its ID.
   *
   * @param {string} id - The ID of the soilAnalysis to be deleted.
   * @returns {Observable<SoilAnalysis>} - Returns an Observable with the deleted soilAnalysis.
   */
  deleteSingleAnalysis(id: string): Observable<SoilAnalysis> {
    return this.http
      .delete<SoilAnalysis>(`${this.apiBaseUrl}api/private/v1/soilAnalysis/${id}`)
      .pipe(map((response) => response));
  }

  /**
   * Deletes a group of soil analyses identified by UUID.
   *
   * @param {string} UUID - The UUID of the group to be deleted.
   * @returns {Observable<SoilAnalysis>} - Returns an Observable with the deleted soil analyses.
   */
  deleteGroupAnalyses(UUID: string): Observable<SoilAnalysis> {
    return this.http.delete<SoilAnalysis>(
      this.apiBaseUrl + `api/private/v1/soilAnalysis/deleteSoilAnalysisGrouped/${UUID}`
    );
  }

  /**
   * Gets the presigned URL for uploading or downloading soil analysis files.
   *
   * @param {Array<{id: string, name: string}>} analysesInfo - An array of objects containing the ID of the soil analysis and the key used to save the file.
   * @param {string} type - The type of presigned URL to generate (upload or download).
   * @returns {Observable<string>} - Returns an Observable with the presigned URL.
   */
  getPresignedUrl(
    analysesInfo: { id: string; key: string }[],
    type: string
  ): Observable<PreSignedResponse> {
    return this.http.post<PreSignedResponse>(
      this.apiBaseUrl + `api/private/v1/soilAnalysis/generatePreSignedUrl?typePreSigned=${type}`,
      analysesInfo
    );
  }

  /**
   * This method is used to delete the file associated with a soil analysis.
   *
   * @param {string} id - The ID of the soil analysis.
   * @returns {boolean} - Return true if the status is ok.
   */
  deleteSoilAnalysisFile(id: string): Observable<any> {
    return this.http.delete(this.apiBaseUrl + `api/private/v1/soilAnalysis/${id}/file`).pipe(
      map(() => {
        return true; // Return true on successful delete (status 200)
      })
    );
  }

  /**
   * This method is used to upload a file to S3 using a pre-signed URL.
   *
   * @param {File} file - The file to be uploaded.
   * @param {string} preSignedUrl - The pre-signed URL for uploading the file.
   * @returns {Observable<any>} - Returns an Observable with the upload response.
   */
  uploadToS3(file: File, preSignedUrl: string): Observable<any> {
    // Create a URL object
    const url = new URL(preSignedUrl);

    const headers = new HttpHeaders({
      'Content-Type': file.type,
      'X-Skip-Interceptor': 'true',
    });

    return this.http.put(url.toString(), file, { headers });
  }

  //* Helpers Function */

  /**
   * This method generates a list of grouped soil analyses based on their unique identifiers.
   *
   * @param {SoilAnalysis[]} soilAnalysisList - The list of soil analyses to be grouped.
   * @returns {SoilAnalysesGroup[]} - Returns an array of grouped soil analyses.
   */
  generateGroupedSoilAnalyses(soilAnalysisList: SoilAnalysis[]): SoilAnalysesGroup[] {
    const soilAnalysisGrouped: SoilAnalysesGroup[] = [];

    // Loop through each soil analysis in the list
    soilAnalysisList.forEach((analysis) => {
      // Get or create a group for the analysis
      const { group, isNewGroup } = this.getOrCreateGroup(soilAnalysisGrouped, analysis);

      // If it's a new group, add it to the grouped list
      if (isNewGroup) {
        soilAnalysisGrouped.push(group);
      }

      // Add the current analysis to the group
      group.analyses.push(analysis);
    });

    // Return the grouped soil analyses
    return soilAnalysisGrouped;
  }

  /**
   * This method checks if a group for a given soil analysis exists and creates one if not.
   *
   * @param {SoilAnalysesGroup[]} soilAnalysisGrouped - The list of existing grouped soil analyses.
   * @param {SoilAnalysis} analysis - The soil analysis for which the group is being checked or created.
   * @returns {{ group: SoilAnalysesGroup, isNewGroup: boolean }} - Returns the group and a flag indicating if it is newly created.
   */
  private getOrCreateGroup(
    soilAnalysisGrouped: SoilAnalysesGroup[],
    analysis: SoilAnalysis
  ): { group: SoilAnalysesGroup; isNewGroup: boolean } {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;

    const groupId = analysis.uuid || analysis.id;
    const groupedBy = analysis.uuid ? 'uuid' : 'id';
    let isNewGroup = false;

    // Check if the group for this analysis already exists
    let group = soilAnalysisGrouped.find((group) => group.groupData.id === groupId);

    if (!group) {
      // If the group doesn't exist, create a new one
      isNewGroup = true;
      const groupData: SoilAnalysisGroupData = new SoilAnalysisGroupData();
      groupData.companyId = companyId ? companyId : analysis.companyId;
      groupData.id = groupId;
      groupData.groupedBy = groupedBy;
      groupData.analysesDate = analysis.analysisDate;
      groupData.analysesName = analysis.name;
      groupData.fieldId = analysis.field.id as number;
      groupData.parcelId =
        analysis.parcel && analysis.parcel.id ? (analysis.parcel.id as number) : null;

      group = {
        groupData,
        analyses: [],
      };
    }

    // Return the group and whether it was newly created
    return { group, isNewGroup };
  }

  /**
   * This method retrieves the value of a property from a soil analysis based on a given translation key.
   *
   * @param {SoilAnalysis} analysis - The soil analysis object containing the properties.
   * @param {string} propTranslationKey - The translation key for the property whose value is being retrieved.
   * @returns {number} - Returns the value of the property, or null if not found.
   */
  getValuePropsByPropTranslationKey(analysis: SoilAnalysis, propTranslationKey: string): number {
    if (!this.soilAnalysisElements.getValue()) {
      return null;
    }

    // Find the element matching the translation key
    const foundElement = this.soilAnalysisElements
      .getValue()
      .find((element) => this.getStringAfterLastDot(element.translationKey) === propTranslationKey);

    if (!foundElement) {
      console.warn(`Element with translation key "${propTranslationKey}" not found.`);
      return null;
    }

    // Find the corresponding property in the analysis
    const foundAnalysisProp = analysis.analysisProps.find(
      (prop) => prop.propId === foundElement.id
    );

    // Return the value of the property, or null if not found
    return foundAnalysisProp?.amount ?? null;
  }

  /**
   * This method retrieves the unitId of measure of a property from a soil analysis based on a given translation key.
   *
   * @param {SoilAnalysis} analysis - The soil analysis object containing the properties.
   * @param {string} propTranslationKey - The translation key for the property whose value is being retrieved.
   * @returns {number} - Returns the unit of measure of the property, or null if not found.
   */
  getUnitIdByPropTranslationKey(analysis: SoilAnalysis, propTranslationKey: string): string {
    if (!this.soilAnalysisElements.getValue()) {
      return null;
    }

    // Find the element matching the translation key
    const foundElement = this.soilAnalysisElements
      .getValue()
      .find((element) => this.getStringAfterLastDot(element.translationKey) === propTranslationKey);

    if (!foundElement) {
      console.warn(`Element with translation key "${propTranslationKey}" not found.`);
      return null;
    }

    // Find the corresponding property in the analysis
    const foundAnalysisProp = analysis.analysisProps.find(
      (prop) => prop.propId === foundElement.id
    );

    return foundAnalysisProp?.unitId ?? null;
  }

  /**
   * This method extracts the string after the last dot in a given string.
   *
   * @param {string} str - The string to be processed.
   * @returns {string} - Returns the substring after the last dot, or an empty string if no dot is found.
   */
  getStringAfterLastDot(str: string): string {
    if (str.indexOf('.') !== -1) {
      return str.substring(str.lastIndexOf('.') + 1);
    } else {
      return '';
    }
  }
}
